import { UnstyledButton } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';

import SubHeaderBgr from './SubHeaderBgr';
import SubNavLink from './SubMenuItem';
import type { SubHeaderWrapperProps, WidgetItem, WidgetAction } from './types';
import useEmbeddedStyles from './SubHeaderEmbedded.styles';

type SubHeaderEmbeddedWidgetProps = {
  items?: WidgetItem[];
  actions?: WidgetAction[];
  classes: ReturnType<typeof useEmbeddedStyles>['classes'];
};

function SubHeaderEmbeddedWidget({ items, actions, classes }: SubHeaderEmbeddedWidgetProps) {
  if ((!items && !actions) || (items?.length === 0 && actions?.length === 0)) return null;

  return (
    <div className={classes.subHeaderWidget}>
      {items &&
        items.length > 0 &&
        items.map((item) => (
          <div className={classes.widgetColumn} key={item.label}>
            <Text variant="subhead-x-small" c="gray2.3">
              {item.label}
            </Text>
            <Text variant="subhead-large">{item.value}</Text>
            {item.sublabel && (
              <Text variant="subhead-small" c={item.isPositive ? 'teal.4' : 'red.5'}>
                {item.sublabel}
              </Text>
            )}
          </div>
        ))}

      {items && items.length > 0 && actions && actions.length > 0 && (
        <div className={classes.widgetDivider} />
      )}

      {actions &&
        actions.length > 0 &&
        actions.map(({ Icon, ...action }) => (
          <UnstyledButton
            className={classes.widgetActionButton}
            key={action.label}
            onClick={action.onClick}
            aria-label={action.label}
          >
            <Icon size={24} />
          </UnstyledButton>
        ))}
    </div>
  );
}

type SubHeaderEmbeddedProps = Omit<SubHeaderWrapperProps, 'isSticky'>;

function SubHeaderEmbedded({ title, subMenuItems, widget, isQuickPicks }: SubHeaderEmbeddedProps) {
  const hasWidget = Boolean(widget?.items?.length) || Boolean(widget?.actions?.length);
  const hasSubMenuItems = subMenuItems && subMenuItems.length > 0;
  const { classes } = useEmbeddedStyles({ hasWidget, hasSubMenuItems });

  return (
    <div className={classes.subHeaderContainer}>
      <div className={classes.subHeaderContent}>
        <h1 className={classes.subHeaderTitle}>{title}</h1>

        <SubHeaderEmbeddedWidget
          items={widget?.items}
          actions={widget?.actions}
          classes={classes}
        />

        {hasSubMenuItems && (
          <div className={classes.subNavContainer}>
            {subMenuItems.map((item) => (
              <SubNavLink key={item.href ?? item.value} {...item} />
            ))}
          </div>
        )}
      </div>
      <SubHeaderBgr isQuickPicks={isQuickPicks} />
    </div>
  );
}

export default SubHeaderEmbedded;
